import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import {
  TrendingUp, Mail, MailOpen, MousePointerClick, UserMinus, Users, Loader2,
  RefreshCw, AlertCircle, Eye, Send, Sparkles, X, PhoneCall,
} from 'lucide-react';
import CampaignDrillDown from './CampaignDrillDown';
import CheckinEngagement from './CheckinEngagement';
import OutreachAnalytics from './OutreachAnalytics';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const HG_GREEN = '#48B64C';
const HG_GREEN_LIGHT = '#76BB40';
const HG_GREY = '#888888';
const HG_RED = '#e07070';
const HG_BLUE = '#5b9bd5';

function StatCard({ icon: Icon, label, value, sub, accent = HG_GREEN, testid }) {
  return (
    <div data-testid={testid} className="bg-hg-surface border border-hg-border rounded-sm p-4 lg:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">{label}</div>
          <div className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider mt-2">{value}</div>
          {sub && <div className="text-[11px] text-hg-text-dim font-sans mt-1">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15` }}>
          <Icon size={16} style={{ color: accent }} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}

function OpenersDrillDown({ campaign, onClose }) {
  // Backwards-compat wrapper that delegates to the rich CampaignDrillDown.
  return <CampaignDrillDown campaign={campaign} onClose={onClose} />;
}

function isRecent(iso, hours = 48) {
  if (!iso) return false;
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') || iso.includes('Z') ? '' : 'Z'));
  return (Date.now() - d.getTime()) <= hours * 3600 * 1000;
}

function isSameDay(iso) {
  if (!iso) return false;
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') || iso.includes('Z') ? '' : 'Z'));
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') || iso.includes('Z') ? '' : 'Z'));
    return d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function fmtRelativeDay(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') || iso.includes('Z') ? '' : 'Z'));
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHrs = Math.floor(diffMs / 3600000);
  if (diffHrs < 1) return 'just now';
  if (diffHrs < 24 && now.getDate() === d.getDate()) return `today · ${fmtTime(iso)}`;
  if (diffHrs < 48) return `yesterday · ${fmtTime(iso)}`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function TodaysSendCard({ campaign, onView }) {
  const s = campaign.stats || {};
  // stats rates come from the API as RATIOS (0-1) — x100 for display, matching the table
  const openRate = Math.round((s.open_rate || 0) * 10000) / 100;
  const clickRate = Math.round((s.click_rate || 0) * 10000) / 100;
  const when = (campaign.finished_at || campaign.started_at) || '';
  const sentToday = isSameDay(when);
  const label = sentToday ? `Sent today · ${fmtTime(when)}` : `Most recent send · ${fmtRelativeDay(when)}`;
  return (
    <div
      data-testid="todays-send-card"
      className="bg-gradient-to-br from-hg-green/10 via-hg-surface to-hg-surface border border-hg-green/30 rounded-sm p-5 lg:p-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles size={13} className="text-hg-green" strokeWidth={1.5} />
            <div className="text-[10px] font-sans text-hg-green uppercase tracking-[0.2em]">{label}</div>
          </div>
          <h3 className="font-display text-lg lg:text-xl text-hg-text tracking-wider mt-2 truncate">
            {campaign.subject || campaign.name}
          </h3>
        </div>
        <button
          data-testid="todays-send-view-btn"
          onClick={onView}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 transition-opacity flex-shrink-0"
        >
          <Eye size={12} /> See who engaged
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mt-5">
        <HeroMetric label="Recipients" value={(s.sent || 0).toLocaleString()} icon={Send} />
        <HeroMetric label="Opens" value={(s.opens_count || 0).toLocaleString()} sub={`${openRate}% open rate`} icon={MailOpen} accent="#48B64C" />
        <HeroMetric label="Clicks" value={(s.clicks_count || 0).toLocaleString()} sub={`${clickRate}% click rate`} icon={MousePointerClick} accent="#76BB40" />
        <HeroMetric label="Unsubs" value={(s.unsubscribes_count || 0).toLocaleString()} icon={UserMinus} accent="#e07070" />
      </div>
    </div>
  );
}

function HeroMetric({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-hg-bg/60 backdrop-blur border border-hg-border rounded-sm px-3 py-3">
      <div className="flex items-center gap-1.5">
        <Icon size={11} className="text-hg-text-dim" strokeWidth={1.5} />
        <div className="text-[9px] uppercase tracking-[0.18em] text-hg-text-dim font-sans">{label}</div>
      </div>
      <div className="font-display text-2xl text-hg-text tracking-wider mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-hg-text-dim font-sans mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [openersFor, setOpenersFor] = useState(null);
  const [engagementFor, setEngagementFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('sent');

  const fetchAnalytics = useCallback(async (refresh = false, signal = undefined) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/mailerlite/analytics?status=${statusFilter}&limit=50`, { signal });
      setData(res.data);
      setError('');
    } catch (e) {
      // Superseded/aborted duplicate (e.g. StrictMode double-mount, or a fast
      // re-fetch) — ignore it so it can never clobber good data or surface a
      // spurious error that would blank the view.
      if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      setError(e.response?.data?.detail || 'Failed to load analytics.');
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [statusFilter]);

  // Abort any in-flight request when the effect re-runs or unmounts, so a stale
  // or duplicate fetch can't set error or overwrite freshly loaded data.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchAnalytics(false, ctrl.signal);
    return () => ctrl.abort();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-hg-green" />
      </div>
    );
  }

  // Only blank the whole view when there is NO data to show. If data is already
  // loaded, a transient error renders as a dismissible inline banner (below) and
  // the dashboard stays on screen.
  if (error && !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-5 flex items-center gap-3" data-testid="analytics-error">
        <AlertCircle size={16} className="text-red-400" />
        <span className="text-sm text-red-400 font-sans">{error}</span>
      </div>
    );
  }

  const summary = data?.summary || {};
  const campaigns = data?.campaigns || [];
  const sentCampaigns = campaigns.filter((c) => c.status === 'sent');

  // Build chart data
  const chartCampaigns = (sentCampaigns.length ? sentCampaigns : campaigns).slice(0, 10).reverse();

  // stats rates are RATIOS (0-1) from the API — x100 so charts plot real percentages
  const openRateData = chartCampaigns.map((c) => ({
    name: (c.subject || c.name || c.campaign_id).slice(0, 24),
    opens: c.stats.opens_count || 0,
    clicks: c.stats.clicks_count || 0,
    open_rate: Math.round((c.stats.open_rate || 0) * 10000) / 100,
  }));

  const performanceData = chartCampaigns.map((c) => ({
    name: (c.subject || c.name || c.campaign_id).slice(0, 20),
    open_rate: Math.round((c.stats.open_rate || 0) * 10000) / 100,
    click_rate: Math.round((c.stats.click_rate || 0) * 10000) / 100,
  }));

  // Engagement breakdown pie
  const engagementData = (() => {
    const opens = summary.total_opens || 0;
    const clicks = summary.total_clicks || 0;
    const unsubs = summary.total_unsubscribes || 0;
    const sent = summary.total_recipients || 0;
    const unopened = Math.max(0, sent - opens);
    return [
      { name: 'Opened (no click)', value: Math.max(0, opens - clicks), color: HG_GREEN },
      { name: 'Opened & clicked', value: clicks, color: HG_GREEN_LIGHT },
      { name: 'Unopened', value: unopened, color: '#3a3a3a' },
      { name: 'Unsubscribed', value: unsubs, color: HG_RED },
    ].filter((s) => s.value > 0);
  })();

  const hasSentData = (summary.total_recipients || 0) > 0;

  // Find the most recent real send (excluding test campaigns) within 48h to surface as hero.
  const todaysCampaign = (() => {
    const sortedByTime = sentCampaigns
      .filter((c) => !c.is_group && isRecent(c.finished_at || c.started_at, 48))
      .sort((a, b) => {
        const da = new Date((a.finished_at || a.started_at || '').replace(' ', 'T'));
        const db = new Date((b.finished_at || b.started_at || '').replace(' ', 'T'));
        return db - da;
      });
    if (sortedByTime.length === 0) return null;
    // Prefer the most recent NON-test campaign; fall back to any most-recent if all are tests.
    const isTest = (c) => /\btest\b/i.test(c.name || '') || /\[test\]/i.test(c.subject || '');
    const nonTest = sortedByTime.find((c) => !isTest(c));
    return nonTest || sortedByTime[0];
  })();

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {openersFor && <OpenersDrillDown campaign={openersFor} onClose={() => setOpenersFor(null)} />}
      {engagementFor && (
        <CheckinEngagement
          runId={engagementFor.run_id}
          monthLabel={(engagementFor.name || '').replace(' Fitter Check-in', '')}
          onClose={() => setEngagementFor(null)}
        />
      )}

      {error && data && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm px-4 py-2.5 flex items-center gap-3" data-testid="analytics-inline-error">
          <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-400 font-sans flex-1">{error} — showing the last loaded data.</span>
          <button
            data-testid="analytics-inline-error-dismiss"
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 flex-shrink-0"
            aria-label="Dismiss"
          ><X size={14} /></button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">ANALYTICS</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">Mailerlite campaign performance — opens, clicks, engagement.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-hg-border rounded-sm overflow-hidden text-xs font-sans" data-testid="analytics-status-filter">
            {[
              { id: 'all', label: 'All' },
              { id: 'sent', label: 'Sent' },
              { id: 'draft', label: 'Drafts' },
            ].map((s) => (
              <button
                key={s.id}
                data-testid={`analytics-filter-${s.id}`}
                onClick={() => setStatusFilter(s.id)}
                className={`px-3 py-1.5 transition-colors ${
                  statusFilter === s.id ? 'bg-hg-green text-hg-text' : 'text-hg-text-dim hover:text-hg-text'
                }`}
              >{s.label}</button>
            ))}
          </div>
          <button
            data-testid="analytics-refresh-btn"
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Today's send hero card */}
      {todaysCampaign && (
        <TodaysSendCard campaign={todaysCampaign} onView={() => setOpenersFor(todaysCampaign)} />
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <StatCard
          testid="stat-campaigns"
          icon={Mail}
          label="Campaigns"
          value={summary.total_campaigns || 0}
          sub={`${summary.sent_campaigns || 0} sent`}
        />
        <StatCard
          testid="stat-recipients"
          icon={Users}
          label="Recipients"
          value={(summary.total_recipients || 0).toLocaleString()}
          sub="Total delivered"
          accent={HG_BLUE}
        />
        <StatCard
          testid="stat-open-rate"
          icon={MailOpen}
          label="Avg Open Rate"
          value={`${summary.avg_open_rate || 0}%`}
          sub={`${(summary.total_opens || 0).toLocaleString()} opens`}
          accent={HG_GREEN}
        />
        <StatCard
          testid="stat-click-rate"
          icon={MousePointerClick}
          label="Avg Click Rate"
          value={`${summary.avg_click_rate || 0}%`}
          sub={`${(summary.total_clicks || 0).toLocaleString()} clicks`}
          accent={HG_GREEN_LIGHT}
        />
      </div>

      {!hasSentData && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 flex items-start gap-3" data-testid="no-sent-data-banner">
          <AlertCircle size={16} className="text-hg-green mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-sans text-hg-text">No sent campaigns yet</div>
            <p className="text-xs text-hg-text-dim font-sans mt-1 leading-relaxed">
              Once you push campaigns from Newsletter Studio or Email Studio and send them from your Mailerlite dashboard, opens, clicks, and engagement data will populate here automatically.
            </p>
          </div>
        </div>
      )}

      {/* Chart row */}
      {chartCampaigns.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Opens & clicks bar chart */}
          <div className="bg-hg-surface border border-hg-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={14} className="text-hg-green" strokeWidth={1.5} />
              <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">Opens & Clicks (Last 10)</h3>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={openRateData} margin={{ top: 10, right: 10, left: -10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis dataKey="name" stroke={HG_GREY} tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke={HG_GREY} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: '#1e1e1e', border: '1px solid #333', fontSize: 12 }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: HG_GREEN }}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="opens" name="Opens" fill={HG_GREEN} radius={[2, 2, 0, 0]} />
                <Bar dataKey="clicks" name="Clicks" fill={HG_GREEN_LIGHT} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Engagement pie / Open rate line */}
          <div className="bg-hg-surface border border-hg-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <MailOpen size={14} className="text-hg-green" strokeWidth={1.5} />
              <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">
                {hasSentData ? 'Engagement Breakdown' : 'Open Rate Trend'}
              </h3>
            </div>
            {hasSentData && engagementData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={engagementData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                    {engagementData.map((e, i) => (<Cell key={i} fill={e.color} stroke="#1e1e1e" />))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e1e1e', border: '1px solid #333', fontSize: 12 }} itemStyle={{ color: '#fff' }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={performanceData} margin={{ top: 10, right: 10, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="name" stroke={HG_GREY} tick={{ fontSize: 9 }} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke={HG_GREY} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#1e1e1e', border: '1px solid #333', fontSize: 12 }} itemStyle={{ color: HG_GREEN }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="open_rate" name="Open %" stroke={HG_GREEN} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="click_rate" name="Click %" stroke={HG_GREEN_LIGHT} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Outreach — warm-list letters + recruit drip, per person */}
      <OutreachAnalytics />

      {/* Campaign table */}
      <div className="bg-hg-surface border border-hg-border rounded-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-hg-border">
          <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">Campaign Detail</h3>
          <span className="text-[10px] text-hg-text-dim font-sans">{campaigns.length} campaigns</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans" data-testid="analytics-table">
            <thead>
              <tr className="text-left text-[10px] text-hg-text-dim uppercase tracking-wider border-b border-hg-border">
                <th className="px-4 py-2.5 font-normal">Campaign</th>
                <th className="px-4 py-2.5 font-normal hidden sm:table-cell">Status</th>
                <th className="px-4 py-2.5 font-normal text-right">Sent</th>
                <th className="px-4 py-2.5 font-normal text-right">Opens</th>
                <th className="px-4 py-2.5 font-normal text-right hidden md:table-cell">Open %</th>
                <th className="px-4 py-2.5 font-normal text-right hidden md:table-cell">Clicks</th>
                <th className="px-4 py-2.5 font-normal text-right hidden lg:table-cell">Click %</th>
                <th className="px-4 py-2.5 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-hg-text-dim py-12 text-xs">No campaigns found.</td></tr>
              ) : campaigns.map((c) => (
                <tr key={c.campaign_id} data-testid={`analytics-row-${c.campaign_id}`} className={`border-b border-hg-border/40 hover:bg-hg-bg/40 ${c.is_group ? 'bg-hg-green/5' : ''}`}>
                  <td className="px-4 py-3 max-w-xs truncate text-hg-text">
                    {c.is_group && <PhoneCall size={12} className="inline mr-1.5 text-hg-green" />}
                    {c.subject || c.name || c.campaign_id}
                    {c.is_group && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-hg-green/10 text-hg-green align-middle">
                        {c.campaigns_count} fitters · 1:1
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                      c.status === 'sent' ? 'bg-hg-green/10 text-hg-green' :
                      c.status === 'draft' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-hg-border text-hg-text-dim'
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-hg-text-dim">{(c.stats.sent || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-hg-text">{(c.stats.opens_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-hg-green">{Math.round((c.stats.open_rate || 0) * 10000) / 100}%</td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-hg-text">{(c.stats.clicks_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-hg-text-dim">{Math.round((c.stats.click_rate || 0) * 10000) / 100}%</td>
                  <td className="px-4 py-3 text-right">
                    {c.is_group ? (
                      <button
                        data-testid={`view-engagement-${c.run_id}`}
                        onClick={() => setEngagementFor(c)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-sans border border-hg-green/40 text-hg-green rounded-sm hover:bg-hg-green/10 transition-colors"
                      >
                        <PhoneCall size={11} /> Performance
                      </button>
                    ) : (
                      <button
                        data-testid={`view-openers-${c.campaign_id}`}
                        onClick={() => setOpenersFor(c)}
                        disabled={c.status !== 'sent'}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Eye size={11} /> Details
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unsubscribes / bounces summary */}
      <div className="grid grid-cols-2 gap-3 lg:gap-4">
        <StatCard
          testid="stat-unsubs"
          icon={UserMinus}
          label="Unsubscribes"
          value={(summary.total_unsubscribes || 0).toLocaleString()}
          accent={HG_RED}
        />
        <StatCard
          testid="stat-ctor"
          icon={MousePointerClick}
          label="Click-to-Open"
          value={`${(summary.total_opens || 0) > 0 ? Math.round(((summary.total_clicks || 0) / summary.total_opens) * 100) : 0}%`}
          sub="Clicks ÷ Opens"
          accent={HG_GREEN_LIGHT}
        />
      </div>
    </div>
  );
}
